import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import {
  Loader2, Save, CheckCircle2, ChevronRight, ChevronLeft,
  FileCheck, Send, Star, Clock, AlertCircle, RefreshCw, User,
  CloudUpload, ExternalLink,
} from 'lucide-react';
import { ghlApi } from '../../lib/ghlApi';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const PATIENT_FIELDS = [
  { key: 'contact.front_desk_team', label: 'Front Desk Agent' },
  { key: 'contact.new_patient_status', label: 'New Patient Status' },
  { key: 'contact.contact_status', label: 'Patient Status' },
  { key: 'contact.appointmeent_booked_by', label: 'Assessment Apt Booked By' },
  { key: 'contact.name_of_therapist_who_did_first_consultation', label: 'Therapist (First Consultation)' },
  { key: 'contact.conversation_summary', label: 'Customer Message', readOnly: true },
  { key: 'contact.clinic_location', label: 'Clinic Location', altKeys: ['contact.clinic', 'contact.location_name'] },
];

const CAMPAIGN_FIELDS = [
  { key: 'contact.marketing_campaign', label: 'Marketing Campaign', altKeys: ['contact.campaign', 'contact.utm_campaign'] },
];

const RIGHT_PANEL_FIELDS = [...PATIENT_FIELDS, ...CAMPAIGN_FIELDS];

const INTAKE_STATUS_FIELD_KEY = 'contact.intake_form_status';

const STEPS = [
  { id: 1, label: 'Patient Info', icon: User },
  { id: 2, label: 'Campaign', icon: Star },
  { id: 3, label: 'Intake & Consent', icon: FileCheck },
  { id: 4, label: 'Google Review', icon: Star },
];

export function PostBookingModal({ open, onOpenChange, bookingData, locationId }) {
  const [step, setStep] = useState(1);
  const [contact, setContact] = useState(null);
  const [contactLoading, setContactLoading] = useState(false);

  const [customFieldMap, setCustomFieldMap] = useState({});
  const allFieldsRef = useRef([]);
  const [panelValues, setPanelValues] = useState({});
  const [panelSaving, setPanelSaving] = useState(false);

  // Track which fields were synced to CRM
  const [syncedFields, setSyncedFields] = useState({});

  // Intake state
  const [intakeStatus, setIntakeStatus] = useState(null);
  const [intakeLastSent, setIntakeLastSent] = useState(null);
  const [intakeSending, setIntakeSending] = useState(false);
  const [intakeSynced, setIntakeSynced] = useState(false);

  // Google review state
  const [reviewSending, setReviewSending] = useState(false);
  const [reviewSent, setReviewSent] = useState(false);

  const contactId = bookingData?.contactId;
  const appointmentId = bookingData?.appointmentId;
  const contactName = bookingData?.contactName || '';

  useEffect(() => {
    if (!open || !contactId || !locationId) return;
    setStep(1);
    setContact(null);
    setPanelValues({});
    setSyncedFields({});
    setIntakeStatus(null);
    setIntakeLastSent(null);
    setIntakeSending(false);
    setIntakeSynced(false);
    setReviewSending(false);
    setReviewSent(false);

    loadFieldDefs().then(() => loadContact(contactId));
    loadIntakeLog(contactId);
  }, [open, contactId, locationId]);

  const loadFieldDefs = async (retries = 2) => {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const resp = await ghlApi.getCustomFields(locationId);
        const fields = resp.data?.customFields || [];
        if (fields.length === 0 && attempt < retries) {
          await new Promise(r => setTimeout(r, 500));
          continue;
        }
        allFieldsRef.current = fields;
        const fMap = {};
        for (const f of fields) {
          if (f.fieldKey) fMap[f.fieldKey] = f;
        }
        setCustomFieldMap(fMap);
        return;
      } catch (err) {
        if (attempt < retries) {
          await new Promise(r => setTimeout(r, 500));
        } else {
          console.error('PostBookingModal: Failed to load custom field definitions', err);
        }
      }
    }
  };

  const loadContact = async (cId) => {
    setContactLoading(true);
    try {
      const resp = await ghlApi.getContact(cId, locationId);
      const c = resp.data?.contact || resp.data || null;
      setContact(c);
      if (c) {
        const fields = allFieldsRef.current;
        const cfs = c.customFields || [];
        const pVals = {};
        for (const cf of (Array.isArray(cfs) ? cfs : [])) {
          const fieldDef = fields.find(f => f.id === cf.id);
          if (!fieldDef) continue;
          for (const rpf of RIGHT_PANEL_FIELDS) {
            if (fieldDef.fieldKey === rpf.key) {
              pVals[rpf.key] = cf.value || '';
            }
            if (rpf.altKeys) {
              for (const ak of rpf.altKeys) {
                if (fieldDef.fieldKey === ak && !pVals[rpf.key]) {
                  pVals[rpf.key] = cf.value || '';
                }
              }
            }
          }
        }
        setPanelValues(pVals);

        // Parse intake status
        let intakeVal = '';
        for (const cf of (Array.isArray(cfs) ? cfs : [])) {
          const fieldDef = fields.find(f => f.id === cf.id);
          if (fieldDef && fieldDef.fieldKey === INTAKE_STATUS_FIELD_KEY) {
            intakeVal = (cf.value || '').toLowerCase().trim();
            break;
          }
        }
        parseIntakeStatus(intakeVal);
      }
    } catch {}
    finally { setContactLoading(false); }
  };

  const parseIntakeStatus = (val) => {
    if (val.includes('consent completed') || val.includes('consent document completed')) setIntakeStatus('consent-completed');
    else if (val.includes('consent sent') || val.includes('consent document sent')) setIntakeStatus('consent-sent');
    else if (val.includes('intake completed')) setIntakeStatus('intake-completed');
    else if (val.includes('intake sent')) setIntakeStatus('intake-sent');
    else if (val.includes('intake requested')) setIntakeStatus('intake-requested');
    else setIntakeStatus('not-sent');
  };

  const loadIntakeLog = async (cId) => {
    try {
      const resp = await ghlApi.getIntakeLog(locationId, cId);
      const logs = resp.data?.logs || [];
      if (logs.length > 0) setIntakeLastSent(logs[0].created_at);
    } catch {}
  };

  const METADATA_KEY_MAP = {
    'contact.front_desk_team': 'front_desk_team',
    'contact.appointmeent_booked_by': 'assessment_booked_by',
    'contact.name_of_therapist_who_did_first_consultation': 'therapist_first_consultation',
    'contact.marketing_campaign': 'marketing_campaign',
  };

  const handleSavePanelField = async (fieldKey, value) => {
    const fieldDef = customFieldMap[fieldKey];
    if (!fieldDef || !contactId) return;
    setPanelSaving(true);
    try {
      await ghlApi.updateContact(contactId, locationId, { custom_fields: [{ id: fieldDef.id, field_value: value }] });
      setPanelValues(prev => ({ ...prev, [fieldKey]: value }));
      setSyncedFields(prev => ({ ...prev, [fieldKey]: true }));
      // Save metadata for analytics
      const metaKey = METADATA_KEY_MAP[fieldKey];
      if (metaKey && value) {
        ghlApi.saveBookingMetadata(locationId, {
          appointment_id: appointmentId || undefined,
          contact_id: contactId,
          [metaKey]: typeof value === 'string' ? value : Array.isArray(value) ? value[0] : String(value),
        }).catch(() => {});
      }
      toast.success('Synced to CRM');
    } catch { toast.error('Failed to sync'); }
    finally { setPanelSaving(false); }
  };

  const handleSendIntake = async () => {
    if (!contactId) return;
    const isResend = intakeStatus && intakeStatus !== 'not-sent';
    setIntakeSending(true);
    try {
      // Get intake field ID for the backend
      const intakeField = allFieldsRef.current.find(f => f.fieldKey === INTAKE_STATUS_FIELD_KEY);

      if (isResend) {
        // Resend: send immediately (patient already got confirmation)
        await ghlApi.updateContactTag(locationId, { contact_id: contactId, tag: 'intake request', mode: 'add' });
        if (intakeField) {
          await ghlApi.updateContact(contactId, locationId, {
            custom_fields: [{ id: intakeField.id, field_value: 'Intake Sent' }],
          });
        }
        await ghlApi.createIntakeLog(locationId, {
          contact_id: contactId, appointment_id: appointmentId || '',
          action: 'resent', sent_by: 'System Admin',
        });
        toast.success('Intake form resent — synced to CRM');
      } else {
        // First send: schedule 5 min delay so confirmation email arrives first
        await ghlApi.scheduleIntakeSend(locationId, {
          contact_id: contactId,
          appointment_id: appointmentId || '',
          sent_by: 'System Admin',
          delay_minutes: 5,
          intake_field_id: intakeField?.id || null,
        });
        toast.success('Intake form will be sent in 5 minutes (after confirmation email)');
      }
      setIntakeStatus('intake-sent');
      setIntakeLastSent(new Date().toISOString());
      setIntakeSynced(true);
    } catch { toast.error('Failed to send intake request'); }
    finally { setIntakeSending(false); }
  };

  const handleRefreshIntakeStatus = async () => {
    if (!contactId) return;
    try {
      const resp = await ghlApi.getContact(contactId, locationId);
      const c = resp.data?.contact || resp.data || null;
      if (c) {
        const fields = allFieldsRef.current;
        const cfs = c.customFields || [];
        let intakeVal = '';
        for (const cf of (Array.isArray(cfs) ? cfs : [])) {
          const fieldDef = fields.find(f => f.id === cf.id);
          if (fieldDef && fieldDef.fieldKey === INTAKE_STATUS_FIELD_KEY) {
            intakeVal = (cf.value || '').toLowerCase().trim();
            break;
          }
        }
        parseIntakeStatus(intakeVal);
        toast.success('Status refreshed from CRM');
      }
    } catch { toast.error('Failed to check status'); }
  };

  const handleSendGoogleReview = async () => {
    if (!contactId) return;
    setReviewSending(true);
    try {
      // Schedule the review request — tag will be added at appointment end time + 60 min
      const aptEndTime = bookingData?.endTime || bookingData?.startTime;
      if (aptEndTime) {
        await ghlApi.createReviewRequest(locationId, {
          appointment_id: bookingData?.appointmentId || '',
          contact_id: contactId,
          contact_name: bookingData?.contactName || '',
          calendar_name: bookingData?.calendarName || '',
          appointment_type: bookingData?.appointmentType || '',
          appointment_end_time: aptEndTime,
          delay_minutes: 60,
          source: 'post_booking_modal',
        });
        setReviewSent(true);
        toast.success('Google review scheduled (tag will be added 1 hour after appointment)');
      } else {
        // Fallback: add tag immediately
        await ghlApi.updateContactTag(locationId, {
          contact_id: contactId,
          tag: 'google review request',
          mode: 'add',
          source: 'post_booking_modal',
          contact_name: bookingData?.contactName || '',
        });
        setReviewSent(true);
        toast.success('Google review request sent to CRM');
      }
    } catch { toast.error('Failed to schedule review request'); }
    finally { setReviewSending(false); }
  };

  const canGoNext = step < 4;
  const canGoBack = step > 1;

  const formatIntakeTimestamp = (ts) => {
    if (!ts) return null;
    try {
      const d = new Date(ts);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
        ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch { return ts; }
  };

  const getIntakeStatusDisplay = () => {
    switch (intakeStatus) {
      case 'consent-completed': return { label: 'Consent Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 };
      case 'consent-sent': return { label: 'Consent Sent', color: 'bg-blue-100 text-blue-700', icon: Send };
      case 'intake-completed': return { label: 'Intake Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 };
      case 'intake-sent': return { label: 'Intake Sent', color: 'bg-blue-100 text-blue-700', icon: Send };
      case 'intake-requested': return { label: 'Intake Requested', color: 'bg-amber-100 text-amber-700', icon: Clock };
      default: return { label: 'Not Sent', color: 'bg-slate-100 text-slate-600', icon: AlertCircle };
    }
  };

  // Count synced fields for step 1 indicator
  const syncedCount = Object.keys(syncedFields).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] p-0 gap-0 overflow-hidden rounded-xl" data-testid="post-booking-modal">
        <DialogTitle className="sr-only">Post-Booking Workflow</DialogTitle>
        <DialogDescription className="sr-only">Complete patient information, intake forms, and review request</DialogDescription>

        {/* Header with patient name + stepper */}
        <div className="px-6 pt-5 pb-4 border-b bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900" data-testid="post-booking-title">Appointment Booked</h2>
              <p className="text-sm text-gray-500">{contactName || 'Patient'}</p>
            </div>
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-1" data-testid="step-indicator">
            {STEPS.map((s, idx) => {
              const Icon = s.icon;
              const isActive = s.id === step;
              const isDone = s.id < step;
              const hasSynced = (s.id === 1 && syncedCount > 0) || (s.id === 3 && intakeSynced) || (s.id === 4 && reviewSent);
              return (
                <React.Fragment key={s.id}>
                  {idx > 0 && <div className={cn('flex-1 h-0.5 rounded', isDone ? 'bg-emerald-400' : 'bg-gray-200')} />}
                  <button
                    onClick={() => setStep(s.id)}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all',
                      isActive ? 'bg-gray-900 text-white shadow-sm' :
                      isDone ? 'bg-emerald-50 text-emerald-700' :
                      'text-gray-400 hover:text-gray-600'
                    )}
                    data-testid={`step-btn-${s.id}`}
                  >
                    {isDone || hasSynced ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                    {s.label}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step content */}
        <div className="px-6 py-5 min-h-[340px] max-h-[480px] overflow-y-auto">
          {contactLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : step === 1 ? (
            <StepPatientInfo
              panelValues={panelValues}
              setPanelValues={setPanelValues}
              customFieldMap={customFieldMap}
              panelSaving={panelSaving}
              handleSavePanelField={handleSavePanelField}
              rightPanelFields={PATIENT_FIELDS}
              syncedFields={syncedFields}
            />
          ) : step === 2 ? (
            <StepCampaign
              panelValues={panelValues}
              setPanelValues={setPanelValues}
              customFieldMap={customFieldMap}
              panelSaving={panelSaving}
              handleSavePanelField={handleSavePanelField}
              campaignFields={CAMPAIGN_FIELDS}
              syncedFields={syncedFields}
            />
          ) : step === 3 ? (
            <StepIntakeConsent
              intakeStatus={intakeStatus}
              intakeLastSent={intakeLastSent}
              intakeSending={intakeSending}
              intakeSynced={intakeSynced}
              onSendIntake={handleSendIntake}
              onRefreshStatus={handleRefreshIntakeStatus}
              getStatusDisplay={getIntakeStatusDisplay}
              formatTimestamp={formatIntakeTimestamp}
            />
          ) : (
            <StepGoogleReview
              reviewSending={reviewSending}
              reviewSent={reviewSent}
              onSendReview={handleSendGoogleReview}
              contactName={contactName}
            />
          )}
        </div>

        {/* Footer with navigation */}
        <div className="px-6 py-4 border-t bg-gray-50/50 flex items-center justify-between">
          <div>
            {canGoBack && (
              <Button variant="outline" size="sm" onClick={() => setStep(s => s - 1)} data-testid="step-back-btn">
                <ChevronLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} data-testid="post-booking-close-btn">
              {step === 4 ? 'Done' : 'Skip & Close'}
            </Button>
            {canGoNext && (
              <Button size="sm" onClick={() => setStep(s => s + 1)} data-testid="step-next-btn">
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function CrmSyncBadge({ synced }) {
  if (!synced) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full" data-testid="crm-sync-badge">
      <CloudUpload className="h-2.5 w-2.5" /> CRM Synced
    </span>
  );
}


function StepPatientInfo({ panelValues, setPanelValues, customFieldMap, panelSaving, handleSavePanelField, rightPanelFields, syncedFields }) {
  return (
    <div data-testid="step-patient-info">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-bold text-gray-800">Patient Field Information</h3>
        <div className="flex items-center gap-1 text-[10px] text-gray-400">
          <CloudUpload className="h-3 w-3" /> Each save syncs directly to CRM
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-4">Update the patient's custom fields. Changes are saved directly to the CRM contact.</p>
      <div className="space-y-3">
        {rightPanelFields.map(rpf => {
          const fieldDef = customFieldMap[rpf.key] || (rpf.altKeys ? rpf.altKeys.reduce((found, ak) => found || customFieldMap[ak], null) : null);
          const hasPicklist = fieldDef?.picklistOptions?.length > 0;
          const isMulti = fieldDef?.dataType === 'MULTIPLE_OPTIONS';
          const rawValue = panelValues[rpf.key];
          const value = Array.isArray(rawValue) ? rawValue.join(', ') : (rawValue || '');
          const selectValue = Array.isArray(rawValue) ? (rawValue[0] || '') : (rawValue || '');
          const isLongText = rpf.key === 'contact.conversation_summary';
          const isReadOnly = rpf.readOnly === true;
          const isCampaign = rpf.key === 'contact.marketing_campaign';
          const isSynced = syncedFields[rpf.key];

          return (
            <div key={rpf.key} className={cn(isCampaign && 'p-3 rounded-lg bg-blue-50/50 border border-blue-100')}>
              <div className="flex items-center gap-2 mb-1.5">
                <label className="text-[11px] font-semibold text-gray-600">
                  {rpf.label}
                </label>
                {isCampaign && <Badge variant="outline" className="text-[9px] py-0 border-blue-200 text-blue-600">Campaign</Badge>}
                {isReadOnly && <Badge variant="outline" className="text-[9px] py-0 border-gray-200 text-gray-400">From CRM</Badge>}
                <CrmSyncBadge synced={isSynced} />
              </div>
              {isReadOnly ? (
                <div className="px-3 py-2 bg-gray-50 rounded-md border text-xs text-gray-700 min-h-[40px] whitespace-pre-wrap" data-testid={`field-${rpf.key}`}>
                  {value || <span className="text-gray-400 italic">No data</span>}
                </div>
              ) : hasPicklist ? (
                <Select value={selectValue} onValueChange={v => {
                  const newVal = v === '__none__' ? '' : (isMulti ? [v] : v);
                  setPanelValues(prev => ({ ...prev, [rpf.key]: newVal }));
                  setTimeout(() => handleSavePanelField(rpf.key, newVal), 0);
                }}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`field-${rpf.key}`}><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- None --</SelectItem>
                    {fieldDef.picklistOptions.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <div className="flex gap-1.5">
                  <Input
                    value={value}
                    onChange={e => setPanelValues(prev => ({ ...prev, [rpf.key]: e.target.value }))}
                    className="h-8 text-xs flex-1"
                    data-testid={`field-${rpf.key}`}
                  />
                  <Button variant="outline" size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => handleSavePanelField(rpf.key, panelValues[rpf.key])} disabled={panelSaving} title="Save to CRM" data-testid={`save-${rpf.key}`}>
                    {panelSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <CloudUpload className="h-3 w-3" />}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function StepCampaign({ panelValues, setPanelValues, customFieldMap, panelSaving, handleSavePanelField, campaignFields, syncedFields }) {
  return (
    <div data-testid="step-campaign">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-bold text-gray-800">Marketing Campaign</h3>
        <div className="flex items-center gap-1 text-[10px] text-gray-400">
          <CloudUpload className="h-3 w-3" /> Syncs to CRM
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-5">Select the marketing campaign that brought this patient in. This helps track lead sources and ROI.</p>
      <div className="space-y-4">
        {campaignFields.map(rpf => {
          const fieldDef = customFieldMap[rpf.key] || (rpf.altKeys ? rpf.altKeys.reduce((found, ak) => found || customFieldMap[ak], null) : null);
          const hasPicklist = fieldDef?.picklistOptions?.length > 0;
          const isMulti = fieldDef?.dataType === 'MULTIPLE_OPTIONS';
          const rawValue = panelValues[rpf.key];
          const selectValue = Array.isArray(rawValue) ? (rawValue[0] || '') : (rawValue || '');
          const value = Array.isArray(rawValue) ? rawValue.join(', ') : (rawValue || '');
          const isSynced = syncedFields[rpf.key];

          return (
            <div key={rpf.key} className="p-4 rounded-xl bg-blue-50/50 border border-blue-100">
              <div className="flex items-center gap-2 mb-2">
                <label className="text-xs font-semibold text-gray-700">{rpf.label}</label>
                <CrmSyncBadge synced={isSynced} />
              </div>
              {hasPicklist ? (
                <Select value={selectValue} onValueChange={v => {
                  const newVal = v === '__none__' ? '' : (isMulti ? [v] : v);
                  setPanelValues(prev => ({ ...prev, [rpf.key]: newVal }));
                  setTimeout(() => handleSavePanelField(rpf.key, newVal), 0);
                }}>
                  <SelectTrigger className="h-10 text-sm" data-testid={`field-${rpf.key}`}><SelectValue placeholder="Select campaign..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- None --</SelectItem>
                    {fieldDef.picklistOptions.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={value}
                    onChange={e => setPanelValues(prev => ({ ...prev, [rpf.key]: e.target.value }))}
                    className="h-10 text-sm flex-1"
                    placeholder="Enter campaign name..."
                    data-testid={`field-${rpf.key}`}
                  />
                  <Button variant="outline" className="h-10 gap-1.5 text-xs" onClick={() => handleSavePanelField(rpf.key, panelValues[rpf.key])} disabled={panelSaving} data-testid={`save-${rpf.key}`}>
                    {panelSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <CloudUpload className="h-3 w-3" />} Save
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function StepIntakeConsent({ intakeStatus, intakeLastSent, intakeSending, intakeSynced, onSendIntake, onRefreshStatus, getStatusDisplay, formatTimestamp }) {
  const statusInfo = getStatusDisplay();
  const StatusIcon = statusInfo.icon;
  const isComplete = intakeStatus === 'consent-completed' || intakeStatus === 'intake-completed';

  return (
    <div data-testid="step-intake-consent">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-bold text-gray-800">Intake & Consent Document</h3>
        {intakeSynced && <CrmSyncBadge synced />}
      </div>
      <p className="text-xs text-gray-500 mb-5">Check the patient's intake form status and send or resend if needed. Sending adds an "intake request" tag and updates the status in the CRM.</p>

      {/* Status card */}
      <div className={cn(
        'rounded-xl border p-5 mb-5',
        isComplete ? 'bg-emerald-50/50 border-emerald-200' : 'bg-white border-gray-200'
      )} data-testid="intake-status-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className={cn('h-9 w-9 rounded-lg flex items-center justify-center', statusInfo.color)}>
              <StatusIcon className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-800">Current Status</p>
              <Badge className={cn('mt-0.5 text-[10px]', statusInfo.color)} data-testid="intake-status-badge">
                {statusInfo.label}
              </Badge>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onRefreshStatus} title="Refresh from CRM" data-testid="refresh-intake-status">
            <RefreshCw className="h-3.5 w-3.5 text-gray-400" />
          </Button>
        </div>
        {intakeLastSent && (
          <p className="text-[11px] text-gray-500 flex items-center gap-1.5 mb-3" data-testid="intake-last-sent">
            <Clock className="h-3 w-3" /> Last sent: {formatTimestamp(intakeLastSent)}
          </p>
        )}
        {isComplete && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-emerald-100/60 mb-3" data-testid="intake-completed-banner">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <span className="text-xs font-medium text-emerald-700">All forms have been completed by the patient.</span>
          </div>
        )}

        {/* CRM sync info */}
        <div className="flex items-center gap-1.5 text-[10px] text-gray-400 mt-2">
          <CloudUpload className="h-3 w-3" />
          <span>Sends "intake request" tag + updates intake status field in CRM</span>
        </div>
      </div>

      {/* Send button */}
      <Button
        className="w-full h-10 text-sm font-semibold gap-2"
        variant={isComplete ? 'outline' : 'default'}
        onClick={onSendIntake}
        disabled={intakeSending}
        data-testid="send-intake-btn"
      >
        {intakeSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {intakeStatus === 'not-sent' ? 'Send Intake Form' : '(Re)send Intake Form'}
      </Button>
    </div>
  );
}


function StepGoogleReview({ reviewSending, reviewSent, onSendReview, contactName }) {
  return (
    <div data-testid="step-google-review">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-bold text-gray-800">Google Review Request</h3>
        {reviewSent && <CrmSyncBadge synced />}
      </div>
      <p className="text-xs text-gray-500 mb-5">Send a Google review request to the patient. This adds a "google review request" tag to their CRM contact, triggering the review workflow.</p>

      <div className={cn(
        'rounded-xl border p-6 text-center mb-5',
        reviewSent ? 'bg-emerald-50/50 border-emerald-200' : 'bg-amber-50/30 border-amber-100'
      )} data-testid="review-card">
        <div className={cn(
          'h-14 w-14 rounded-2xl flex items-center justify-center mx-auto mb-3',
          reviewSent ? 'bg-emerald-100' : 'bg-amber-100'
        )}>
          {reviewSent
            ? <CheckCircle2 className="h-7 w-7 text-emerald-600" />
            : <Star className="h-7 w-7 text-amber-600" />
          }
        </div>
        {reviewSent ? (
          <>
            <p className="text-sm font-bold text-emerald-800 mb-1">Review Request Synced to CRM</p>
            <p className="text-xs text-emerald-600">
              A "google review request" tag has been added to {contactName || 'the patient'}'s CRM contact.
            </p>
          </>
        ) : (
          <>
            <p className="text-sm font-bold text-gray-800 mb-1">Request a Google Review</p>
            <p className="text-xs text-gray-500">
              This will add a "google review request" tag to {contactName || 'the patient'}'s CRM contact, triggering the review workflow.
            </p>
          </>
        )}

        {/* CRM sync info */}
        <div className="flex items-center justify-center gap-1.5 text-[10px] text-gray-400 mt-3">
          <CloudUpload className="h-3 w-3" />
          <span>Adds "google review request" tag to CRM contact</span>
        </div>
      </div>

      <Button
        className="w-full h-10 text-sm font-semibold gap-2"
        variant={reviewSent ? 'outline' : 'default'}
        onClick={onSendReview}
        disabled={reviewSending || reviewSent}
        data-testid="send-review-btn"
      >
        {reviewSending ? <Loader2 className="h-4 w-4 animate-spin" /> : reviewSent ? <CheckCircle2 className="h-4 w-4" /> : <Star className="h-4 w-4" />}
        {reviewSent ? 'Review Request Sent' : 'Send Google Review Request'}
      </Button>
    </div>
  );
}
